/**
 * kevia-insights/index.js
 *
 * Motor de insights pastorales determinísticos.
 * No requiere LLM — detecta señales desde datos existentes.
 *
 * Cada insight tiene:
 *   id            — identificador único
 *   severity      — "critical" | "high" | "medium" | "low"
 *   confidence    — 0.0–1.0
 *   explanation   — oración humana legible (discernimiento, no analítica)
 *   suggestedTone — tono sugerido al LLM
 *   mood          — mood visual sugerido para el avatar
 */

import { getDaysSince } from "../date";
export { generateSessionInsight } from "./sessionInsights";

export function generateInsights(entityType, entity, opts = {}) {
  const generators = {
    person: generatePersonInsights,
    family: generateFamilyInsights,
    church: generateChurchInsights,
    visit:  generateVisitInsights,
    prayer: generatePrayerInsights,
  };
  const gen = generators[entityType];
  if (!gen || !entity?.id) return [];
  return gen(entity, opts).filter(Boolean);
}

/* ─── PERSONA ────────────────────────────────────────────────────────────── */

function generatePersonInsights(person, opts) {
  const { lastVisit, activePrayers = 0, urgentPrayers = 0 } = opts;
  const days = lastVisit?.date ? getDaysSince(lastVisit.date) : null;
  const out  = [];

  if (days === null) {
    out.push({ id: "no-visit", severity: "high", confidence: 0.90,
      explanation: "Sin visitas pastorales registradas",
      suggestedTone: "followup_urgent", mood: "reflective" });
  } else if (days > 90) {
    out.push({ id: "overdue-90", severity: "high", confidence: 0.95,
      explanation: `${days} días sin visita pastoral`,
      suggestedTone: "followup_urgent", mood: "reflective" });
  } else if (days > 45) {
    out.push({ id: "overdue-45", severity: "medium", confidence: 0.85,
      explanation: `${days} días sin visita pastoral`,
      suggestedTone: "followup_needed", mood: "reflective" });
  }

  if (urgentPrayers > 0) {
    out.push({ id: "urgent-prayers", severity: "high", confidence: 1.0,
      explanation: plural(urgentPrayers, "oración urgente activa", "oraciones urgentes activas"),
      suggestedTone: "sensitive", mood: "urgent" });
  } else if (activePrayers > 2) {
    out.push({ id: "active-prayers", severity: "low", confidence: 0.80,
      explanation: plural(activePrayers, "petición de oración activa", "peticiones de oración activas"),
      suggestedTone: "pastoral", mood: "pastoral-care" });
  }

  if (person.baptism_status === "en_proceso" && days !== null && days > 30) {
    out.push({ id: "baptism-stalled", severity: "medium", confidence: 0.70,
      explanation: "Proceso bautismal activo sin seguimiento reciente",
      suggestedTone: "accompany", mood: "pastoral-care" });
  }

  return out;
}

/* ─── FAMILIA ────────────────────────────────────────────────────────────── */

function generateFamilyInsights(family, opts) {
  const { lastVisit, membersCount = 0, activePrayers = 0, urgentPrayers = 0 } = opts;
  const days = lastVisit?.date ? getDaysSince(lastVisit.date) : null;
  const out  = [];

  if (days === null) {
    out.push({ id: "no-visit", severity: "high", confidence: 0.90,
      explanation: "Familia sin visitas pastorales registradas",
      suggestedTone: "followup_urgent", mood: "reflective" });
  } else if (days > 90) {
    out.push({ id: "overdue-90", severity: "high", confidence: 0.95,
      explanation: `${days} días sin visita a esta familia`,
      suggestedTone: "followup_urgent", mood: "reflective" });
  } else if (days > 45) {
    out.push({ id: "overdue-45", severity: "medium", confidence: 0.85,
      explanation: `${days} días sin visita a esta familia`,
      suggestedTone: "followup_needed", mood: "reflective" });
  }

  if (urgentPrayers > 0) {
    out.push({ id: "urgent-prayers", severity: "high", confidence: 1.0,
      explanation: plural(urgentPrayers, "oración urgente activa en esta familia", "oraciones urgentes activas en esta familia"),
      suggestedTone: "sensitive", mood: "urgent" });
  }

  if (membersCount > 0 && urgentPrayers > 1) {
    out.push({ id: "multiple-sensitive", severity: "high", confidence: 0.80,
      explanation: "Familia con múltiples señales pastorales sensibles",
      suggestedTone: "sensitive", mood: "pastoral-care" });
  }

  return out;
}

/* ─── IGLESIA ────────────────────────────────────────────────────────────── */

function generateChurchInsights(church, opts) {
  const { lastVisitDays = null, familiesCount = 0, baptismsThisYear = 0 } = opts;
  const out = [];

  if (lastVisitDays === null) {
    out.push({ id: "no-visit", severity: "high", confidence: 0.90,
      explanation: "Iglesia sin visita pastoral registrada",
      suggestedTone: "followup_urgent", mood: "reflective" });
  } else if (lastVisitDays > 90) {
    out.push({ id: "overdue-90", severity: "high", confidence: 0.90,
      explanation: `${lastVisitDays} días sin visita pastoral registrada`,
      suggestedTone: "followup_urgent", mood: "reflective" });
  }

  if (baptismsThisYear > 0) {
    out.push({ id: "baptisms", severity: "low", confidence: 1.0,
      explanation: plural(baptismsThisYear, "bautismo este año", "bautismos este año"),
      suggestedTone: "celebration", mood: "celebration" });
  }

  if (familiesCount === 0) {
    out.push({ id: "no-families", severity: "medium", confidence: 0.85,
      explanation: "Iglesia sin familias registradas",
      suggestedTone: "accompany", mood: "pastoral-care" });
  }

  return out;
}

/* ─── VISITA ─────────────────────────────────────────────────────────────── */

function generateVisitInsights(visit, opts) {
  const { typeLabel = "" } = opts;
  const out = [];

  if (visit.type === "duelo") {
    out.push({ id: "grief-visit", severity: "high", confidence: 1.0,
      explanation: "Visita de duelo — momento de acompañamiento profundo",
      suggestedTone: "sensitive", mood: "grief" });
  } else if (visit.type === "crisis") {
    out.push({ id: "crisis-visit", severity: "high", confidence: 1.0,
      explanation: "Visita de crisis — situación que requiere cuidado pastoral",
      suggestedTone: "sensitive", mood: "urgent" });
  }

  if (visit.status === "pendiente") {
    out.push({ id: "visit-pending", severity: "medium", confidence: 0.90,
      explanation: "Visita con seguimiento pendiente",
      suggestedTone: "followup_needed", mood: "reflective" });
  }

  return out;
}

/* ─── ORACIÓN ────────────────────────────────────────────────────────────── */

function generatePrayerInsights(prayer) {
  const out = [];

  if (prayer.priority === "alta" && prayer.status === "activa") {
    out.push({ id: "urgent-prayer", severity: "high", confidence: 1.0,
      explanation: "Petición urgente activa — requiere atención pastoral",
      suggestedTone: "sensitive", mood: "urgent" });
  }

  if (prayer.status === "respondida") {
    out.push({ id: "answered", severity: "low", confidence: 1.0,
      explanation: "Oración respondida — momento de gratitud",
      suggestedTone: "celebration", mood: "celebration" });
  }

  return out;
}

/* ─── UTIL ───────────────────────────────────────────────────────────────── */

function plural(n, singular, plural) {
  return n === 1 ? `${n} ${singular}` : `${n} ${plural}`;
}
